import React, { useContext, useEffect, useState } from 'react'

// Libs
import { isEqual, size, sumBy } from 'lodash'
import { ChevronDown, Loader2Icon, MessageCircle } from 'lucide-react'

import { HandleModalContext } from '../../../../layout/mainlayout/MainLayout'

// Components
import { BackNumber } from '../components/BackNumber'
import { ButtonSwitchHorse } from '../components/ButtonSwitchHorse'
import { IssueField, IssuesTableName, TableName } from '../components/TableName'
import { HorseTrainer } from '../../../../components/rider/HorseTrainer/HorseTrainer'
import { IconDropDown } from '../../../../components/icons/IconDropDown'
import { Button } from '../../../../components/ui/button'

// Hooks
import { useEventRiderTeamMembers } from '../../../../hooks/useEventRiderTeamMembers'
import { useChatClient } from '../../../messages/components/withStreamClient'
import customImageComponent from '../../../../components/common/CustomImageComponent'

// Services
import UsersDocumentsService from '../../../../services/firebase-service/users-documents/UsersDocumentsService'
import EventRegisteredUsersService from '../../../../services/firebase-service/event-registered-users/EventRegisteredUsersService'
import RiderTeamMemberService from '../../../../services/firebase-service/rider-team-member/RiderTeamMemberService'
import EventReviewPublishService from '../../../../services/firebase-service/event-review-publish/EventReviewPublishService'
import UserService from '../../../../services/firebase-service/users/userService'
import RegistrationFeesService from '../../../../services/firebase-service/registration-fees/RegistrationFeesService'
import HorseCompetitionPaperworkService from '../../../../services/firebase-service/horse-competition-paperwork/HorseCompetitionPaperworkService'
import HorsesService from '../../../../services/firebase-service/horses/HorsesService'
import RegistrationDyDayService from '../../../../services/firebase-service/registration-by-day/RegistrationDyDayService'

// Helpers
import { generateName } from '../../../../helpers/helpers'
import { getAdminEntries, getRiderTeamMembersByRole } from '../../../../helpers/riders'
import { cn } from '../../../../lib/utils'

// Redux
import { useAppDispatch } from '../../../../store/hooks'
import { organizerSliceAcs } from '../../../../store/organizer/organizerSlice'

// Models
import { UserDocumentModel } from '../../../../models/user-documents/user-documents.model'

// Types
import { IRiderTeamMemberInterface } from '../../../../models/rider-team-member/riderTeamMember.interface'
import { TEventRegisteredUsers } from '../../../../models/event-registered-users/event-registered-users.interface'
import { IEventReviewPublish } from '../../../../models/event-review-publish/event-review-publish.interface'
import { IUserInterface } from '../../../../models/users/user.interface'
import { IUserDocument } from '../../../../models/user-documents/user-documents.interface'
import { IRegistrationFeesInterface } from '../../../../models/registration-fees/registrationFees.interface'
import { IHorseCompetitorDocument } from '../../../../models/horse-competiton-paperwork/horse-competiton-paperwork-interface'
import { IRider } from '../../../../types/entryRider'
import { IHorseData } from '../../../../models/horse/horse.interface'

// Constants
import { CONST } from '../../../../const/const'
import { MODAL_CONSTS } from '../../../../const/modal-const'
import { PAYMENT_TYPE } from '../../../../types/payment'
import {
  HORSE_NAME_DEFAULT,
  IRegistrationByDayInterface,
} from '../../../../models/registrations-by-day/registrationByDay.interface'
import { RIDER_TABS } from '../../../../components/modals/InfoModals/RiderInfoModal/RiderInfoModal'
import { MEMBER_ROLES } from '../../../../const/ui/ui-register.const'

const UNKNOWN = 'Unknown'

export enum ETabId {
  entries,
  spectators,
  draft,
  classes,
  totals,
  // checkIn,
}

interface ITab {
  tab: string
  id: ETabId
  disabled?: boolean
}
export const MANAGE_CLINIC_TABS: ITab[] = [
  { tab: 'Entries', id: ETabId.entries },
  { tab: 'Results', id: ETabId.classes },
  { tab: 'Spectators', id: ETabId.spectators },
  { tab: 'Totals', id: ETabId.totals },
]

export const ALL_CLASSES = { value: 'all', label: 'All Classes' }

export enum EStatuses {
  all = 'All',
  noIssues = 'No issues',
  issues = 'Issues',
  scratched = 'Scratched',
  draft = 'Draft',
}
export const ALL_STATUSES = { value: EStatuses.all, label: EStatuses.all }

enum ESorts {
  none,
  rider,
  horse,
  back,
}

const getNoIssues = (items: IRider[]) => {
  return items.filter(
    (item) =>
      (!item.showRequiredIssues || item.requiredIssues <= 0) &&
      (!item.showClassesIssues || item.classesIssues <= 0) &&
      (!item.showFeesIssues || item.feesIssues <= 0) &&
      (!item.showPaperworkIssues || item.paperworkIssues <= 0) &&
      (!item.showTeamIssues || item.teamIssues <= 0) &&
      (!item.showHorsesIssues || item.horsesIssues <= 0)
  )
}

const geIssues = (items: IRider[]) => {
  return items.filter(
    (item) =>
      (item.showRequiredIssues && item.requiredIssues > 0) ||
      (item.showClassesIssues && item.classesIssues > 0) ||
      (item.showFeesIssues && item.feesIssues > 0) ||
      (item.showPaperworkIssues && item.paperworkIssues > 0) ||
      (item.showTeamIssues && item.teamIssues > 0) ||
      (item.showHorsesIssues && item.horsesIssues > 0)
  )
}

const ridersWithDraft = (registeredUsers: TEventRegisteredUsers[]) => {
  const draft: IRider[] = []

  registeredUsers.forEach((registeredUser) => {
    const classes: IRider[] = []
    registeredUser.cartItems?.classes
      ?.filter((item) => !item.isDivision)
      .forEach((item) => {
        const hasRider = draft.find(
          (rd) =>
            rd.registrationDocId === item.registrationDocId &&
            rd.riderId === item.riderId &&
            isEqual(rd.horsesSelected, item.horsesSelected)
        )

        if (!hasRider) {
          draft.push({
            ...item,
            requiredIssues: 0,
            showRequiredIssues: false,
            classesIssues: 0,
            showClassesIssues: false,
            feesIssues: 0,
            showFeesIssues: false,
            paperworkIssues: 0,
            showPaperworkIssues: false,
            teamIssues: 0,
            showTeamIssues: false,
            horsesIssues: 0,
            showHorsesIssues: false,
            classes: [],
            scratchedClasses: [],
            riderTeamMember: null,
          })
        }
      })
    draft.push(...classes)
  })

  return draft
}

export const useManageClinicNOtherAdminTabNew = (eventId: string) => {
  const { eventTeamMembers, eventTeamMembersLoading, getEventRiderTeamMembers } =
    useEventRiderTeamMembers(eventId)

  const handleModal = useContext(HandleModalContext)?.handleModal
  const { createChatEvent } = useChatClient()

  const [ridersClasses, setRidersClasses] = useState<(typeof ALL_CLASSES)[]>([])
  const [activeTab, setActiveTab] = useState<ETabId>(ETabId.entries)
  const [activeClass, setActiveClass] = useState({ value: '', label: '' })
  const [activeStatus, setActiveStatus] = useState<{ value: string; label: string } | null>(null)
  const [filteredRiders, setFilteredRiders] = useState<IRider[]>([])
  const [draftRiders, setDraftRiders] = useState<IRider[]>([])
  const [registrationsByDay, setRegistrationByDay] = useState<IRegistrationByDayInterface[]>([])
  const [fees, setFees] = useState<IRegistrationFeesInterface[]>([])
  const [usersDocuments, setUsersDocuments] = useState<IUserDocument[]>([])
  const [allRiders, setAllRiders] = useState<IRider[]>([])
  const [searchNameValue, setSearchNameValue] = useState('')
  const [loading, setLoading] = useState(true)
  const [activeSortType, setActiveSortType] = useState<ESorts>(ESorts.none)
  const [riderTeamMembers, setRiderTeamMembers] = useState<IRiderTeamMemberInterface[]>([])
  const [eventRegisteredUsers, setEventRegisteredUsers] = useState<TEventRegisteredUsers[]>([])
  const [messageLoading, setMessageLoading] = useState('')
  const [users, setUsers] = useState<IUserInterface[]>([])
  const [eventReviewPublish, setEventReviewPublish] = useState<IEventReviewPublish | null>(null)
  const [horseCompetitionPaperwork, setHorseCompetitionPaperwork] = useState<
    IHorseCompetitorDocument[]
  >([])
  const [horses, setHorses] = useState<IHorseData[]>([])
  const [syncLoading, setSyncLoading] = useState(false)

  const sortRidersByScratched = (riders: IRider[]): IRider[] => {
    return riders.sort((a, b) => {
      if (a.isScratched === b.isScratched) return 0
      return a.isScratched ? 1 : -1
    })
  }

  const showRiderModal = (item: IRider, defaultActiveTab?: string) => {
    handleModal?.(true, MODAL_CONSTS.RIDER_INFO, {
      eventId: item.eventId,
      registrarId: item.userId,
      registrationId: item.registrationDocId,
      riderId: item.riderId,
      defaultActiveTab,
      onClose: async () => {
        setSyncLoading(true)
        await getDataFromDb()
        setSyncLoading(false)
      },
    })
  }

  const showRegistrarModal = (item: IRider, defaultActiveTab?: string) => {
    handleModal?.(true, MODAL_CONSTS.REGISTRAR_INFO, {
      eventId: item.eventId,
      registrarId: item.userId,
      registrationId: item.registrationDocId,
      defaultActiveTab,
      onClose: async () => {
        setSyncLoading(true)
        await getDataFromDb()
        setSyncLoading(false)
      },
    })
  }

  const showAddTeamMemberModal = (item: IRider) => {
    const { teamMembers } = getRiderTeamMembersByRole(eventTeamMembers, item, MEMBER_ROLES.TRAINER)

    handleModal?.(true, MODAL_CONSTS.SWITCH_TEAM_MEMBER, {
      role: 'Trainer',
      registrationByDay: item,
      teamMembers: teamMembers,
      eventReviewPublish: eventReviewPublish,
      onSave: async () => {
        await getEventRiderTeamMembers()
        const riderTeamMembersInDb = await RiderTeamMemberService.getByEventId(eventId)
        setRiderTeamMembers([...riderTeamMembersInDb])
      },
    })
  }

  const onMessage = async (item: IRider) => {
    setMessageLoading(item.id || '')
    if (item.userId) {
      await createChatEvent(
        [
          {
            id: item.userId,
            name: item.riderName ?? '',
            role: 'Rider',
            vertical: 'exhibitors',
          },
        ],
        eventId,
        { eventId: eventId }
      ).then(() => {
        const url = `${CONST.ROUTES.MANAGE.CLINIC_N_OTHER.URL}/${eventId}/messages`
        window.open(url, '_blank')
      })
    }
    setMessageLoading('')
  }

  const tableColumns = [
    {
      name: (
        <TableName
          title={'Back #'}
          titleAdds={
            !searchNameValue.length &&
            (activeStatus?.value === ALL_STATUSES.value || !activeStatus?.value) &&
            activeClass.value === ALL_CLASSES.value ? (
              <span
                onClick={() => {
                  handleModal?.(true, MODAL_CONSTS.AUTO_ASSIGN_MODAL, {
                    riders: allRiders,
                    cb: (arrValues: number[]) => {
                      const newRiders = allRiders
                      arrValues.forEach((num, i) => {
                        if (newRiders[i]) newRiders[i].backNumber = num
                      })
                      setFilteredRiders(newRiders)
                    },
                  })
                }}
                className={'text-[#122B4680] opacity-50 cursor-pointer hover:opacity-90'}
              >
                • Auto
              </span>
            ) : (
              <></>
            )
          }
          btnHandler={
            <button
              onClick={() => sortHandler('backNumber')}
              className={cn(activeSortType === ESorts.back && 'rotate-180')}
            >
              <IconDropDown />
            </button>
          }
        />
      ),
      cell: (row: IRider) => {
        if (
          row.paymentType === PAYMENT_TYPE.DEFAULT &&
          row.paymentStatus === PAYMENT_TYPE.DEFAULT
        ) {
          return (
            <span
              className={
                'whitespace-nowrap text-center w-[40px] text-[14px] font-normal text-SeabiscuitDark200ThemeColor/60'
              }
            >
              Draft
            </span>
          )
        }
        return (
          <BackNumber
            id={row.id || ''}
            backNumber={row.backNumber}
            allRiders={allRiders}
            cbUpdate={(value) => {
              const newRiders = [...allRiders]
              const riderIndex = newRiders.findIndex((r) => r.id === row.id)
              newRiders[riderIndex].backNumber = value
              setFilteredRiders(newRiders)
            }}
            registrationsByDay={registrationsByDay}
          />
        )
      },
      width: '140px',
    },
    {
      name: (
        <TableName
          title="Rider & Registrar"
          btnHandler={
            <button
              onClick={() => sortHandler('riderName')}
              className={cn(activeSortType === ESorts.rider && 'rotate-180')}
            >
              <IconDropDown />
            </button>
          }
        />
      ),
      cell: (row: IRider) => (
        <div className="flex items-center gap-4 w-full">
          <span
            className="p-[2px] bg-[#f6f7fb] my-2 flex items-center rounded-full justify-center shrink-0 hover:opacity-70 transition-all cursor-pointer"
            onClick={() => showRiderModal(row)}
          >
            {customImageComponent(
              row?.riderProfilePicture,
              row.riderName,
              'w-11 h-11 rounded-full'
            )}
          </span>
          <div className="max-w-[calc(100%_-64px)]">
            <span
              className={cn(
                'text-base block text-SeabiscuitDark200ThemeColor font-normal capitalize max-w-[220px] ellipsis hover:underline cursor-pointer',
                row.isScratched && 'line-through'
              )}
              onClick={() => showRiderModal(row)}
            >
              {row.riderName ?? UNKNOWN}
            </span>
            <span
              className="cursor-pointer block text-SeabiscuitDark200ThemeColor/60 text-base  hover:underline"
              onClick={() => showRegistrarModal(row)}
            >
              {row.userId === eventReviewPublish?.owner ? 'Organizer' : row.userName || UNKNOWN}
            </span>
          </div>
        </div>
      ),
      maxWidth: '50%',
      minWidth: '220px',
      conditionalCellStyles: [
        {
          when: () => true,
          style: {
            padding: '0',
          },
        },
      ],
    },
    {
      name: (
        <TableName
          title={'Horse & Trainer'}
          btnHandler={
            <button
              onClick={() => sortHandler('horseName')}
              className={cn(activeSortType === ESorts.horse && 'rotate-180')}
            >
              <IconDropDown />
            </button>
          }
        />
      ),
      cell: (row: IRider) => {
        // Some row data literally has the horseName as "No Horse", handle appropriate styling if this is the case.
        if (row.horseName === HORSE_NAME_DEFAULT) {
          return (
            <div className="flex items-center gap-2 w-full">
              <p className="text-[14px] text-SeabiscuitDark200ThemeColor/60 font-normal ">
                {HORSE_NAME_DEFAULT}
              </p>

              {!row.isScratched && (
                <ButtonSwitchHorse
                  row={row}
                  registrationsByDay={registrationsByDay}
                  setRegistrationByDay={setRegistrationByDay}
                  getEventRiderTeamMembers={getEventRiderTeamMembers}
                />
              )}
            </div>
          )
        }

        if (row.horsesSelected && !row.horsesSelected[0] && row.horseName) {
          return row.horseName
        }

        return (
          <div className="flex gap-2 items-center w-full">
            <span
              className="p-[2px] bg-[#f6f7fb] my-2 flex items-center rounded-full justify-center shrink-0 hover:opacity-70 transition-all cursor-pointer"
              onClick={() => showRiderModal(row, RIDER_TABS.HORSES.value)}
            >
              <img
                src={row.horseProfilePicture ?? ''}
                alt="icons"
                className="object-cover w-11 h-11 rounded-full"
                onError={(e) =>
                  ((e.target as any).src =
                    `https://ui-avatars.com/api/?name=${generateName(row.horseName ?? '')}&background=FFFFFF&format=svg&bold=true&color=BCC6D5&rounded=true`)
                }
              />
            </span>
            <div>
              <div className={'flex items-center gap-2'}>
                <div
                  className="space-x-2 hover:underline cursor-pointer"
                  onClick={() => showRiderModal(row, RIDER_TABS.HORSES.value)}
                >
                  <span className="text-base font-normal text-SeabiscuitDark200ThemeColor">
                    {row.horseName || HORSE_NAME_DEFAULT}
                  </span>
                  <span className="text-black/70">
                    {row.horsesSelected && row.horsesSelected.length > 1
                      ? `+${row.horsesSelected && row.horsesSelected.length - 1} horses`
                      : ''}
                  </span>
                </div>

                {!row.isScratched && (
                  <ButtonSwitchHorse
                    row={row}
                    registrationsByDay={registrationsByDay}
                    setRegistrationByDay={setRegistrationByDay}
                    getEventRiderTeamMembers={getEventRiderTeamMembers}
                  />
                )}
              </div>
              <span
                className="block text-SeabiscuitDark200ThemeColor/60 text-sm hover:underline cursor-pointer"
                onClick={() => showAddTeamMemberModal(row)}
              >
                <HorseTrainer
                  eventTeamMembers={eventTeamMembers}
                  eventTeamMembersLoading={eventTeamMembersLoading}
                  rider={row}
                />
              </span>
            </div>
          </div>
        )
      },
      maxWidth: '50%',
      minWidth: '220px',
      conditionalCellStyles: [
        {
          when: () => true,
          style: {
            padding: '0',
          },
        },
      ],
    },
    {
      name: (
        <IssuesTableName
          name="Required"
          keyName="showRequiredIssues"
          onClick={() => {
            setAllRiders(
              allRiders.map((item) => ({
                ...item,
                showRequiredIssues: !item.showRequiredIssues,
              }))
            )
          }}
          items={filteredRiders}
        />
      ),
      cell: (row: IRider) => (
        <IssueField
          issues={row.requiredIssues}
          showIssues={row.showRequiredIssues}
          onClick={() => showRiderModal(row, RIDER_TABS.PROFILE.value)}
        />
      ),
      width: '100px',
    },
    {
      name: (
        <IssuesTableName
          name="Classes"
          keyName="showClassesIssues"
          onClick={() => {
            setAllRiders(
              allRiders.map((item) => ({
                ...item,
                showClassesIssues: !item.showClassesIssues,
              }))
            )
          }}
          items={filteredRiders}
        />
      ),
      cell: (row: IRider) => (
        <IssueField
          issues={row.classesIssues}
          showIssues={row.showClassesIssues}
          onClick={() => showRiderModal(row, RIDER_TABS.CLASSES.value)}
        />
      ),
      width: '110px',
    },
    {
      name: (
        <IssuesTableName
          name="Fees"
          keyName="showFeesIssues"
          onClick={() => {
            setAllRiders(
              allRiders.map((item) => ({
                ...item,
                showFeesIssues: !item.showFeesIssues,
              }))
            )
          }}
          items={filteredRiders}
        />
      ),
      cell: (row: IRider) => (
        <IssueField
          issues={row.feesIssues}
          showIssues={row.showFeesIssues}
          onClick={() => showRiderModal(row, RIDER_TABS.FEES.value)}
        />
      ),
      width: '100px',
    },
    {
      name: (
        <IssuesTableName
          name="Paperwork"
          keyName="showPaperworkIssues"
          onClick={() => {
            setAllRiders(
              allRiders.map((item) => ({
                ...item,
                showPaperworkIssues: !item.showPaperworkIssues,
              }))
            )
          }}
          items={filteredRiders}
        />
      ),
      cell: (row: IRider) => (
        <IssueField
          issues={row.paperworkIssues}
          showIssues={row.showPaperworkIssues}
          onClick={() => showRiderModal(row, RIDER_TABS.PAPERWORK.value)}
        />
      ),
      width: '120px',
    },
    {
      name: (
        <IssuesTableName
          name="Team"
          keyName="showTeamIssues"
          onClick={() => {
            setAllRiders(
              allRiders.map((item) => ({
                ...item,
                showTeamIssues: !item.showTeamIssues,
              }))
            )
          }}
          items={filteredRiders}
        />
      ),
      cell: (row: IRider) => (
        <IssueField
          issues={row.teamIssues}
          showIssues={row.showTeamIssues}
          onClick={() => showRiderModal(row, RIDER_TABS.TEAM.value)}
        />
      ),
      width: '100px',
    },
    {
      name: (
        <IssuesTableName
          name="Horses"
          keyName="showHorsesIssues"
          onClick={() => {
            setAllRiders(
              allRiders.map((item) => ({
                ...item,
                showHorsesIssues: !item.showHorsesIssues,
              }))
            )
          }}
          items={filteredRiders}
        />
      ),
      cell: (row: IRider) => (
        <IssueField
          issues={row.horsesIssues}
          showIssues={row.showHorsesIssues}
          onClick={() => showRiderModal(row, RIDER_TABS.HORSES.value)}
        />
      ),
      width: '100px',
    },
    {
      name: <TableName title="Manage" centered />,
      cell: (row: IRider) => (
        <div className="flex gap-2 justify-start lg:justify-end w-full">
          <Button
            variant="outline"
            onClick={() => onMessage(row)}
            className="text-black/50 w-[45px] h-[45px] rounded-full"
          >
            {messageLoading === row.id ? (
              <Loader2Icon className="animate-spin w-5" />
            ) : (
              <MessageCircle className="!size-5" />
            )}
          </Button>
          <Button
            variant="secondary"
            onClick={() => showRegistrarModal(row)}
            className="w-[45px] h-[45px] rounded-full"
          >
            <ChevronDown className="!size-5" />
          </Button>
        </div>
      ),
      width: '140px',
    },
  ]

  const dispatch = useAppDispatch()

  useEffect(() => {
    if (eventReviewPublish?.EventFees) {
      const totalClassesLength = sumBy(allRiders, (entry) => size(entry.classes))

      const allClassesLabel = {
        value: ALL_CLASSES.value,
        label: `${ALL_CLASSES.label}  <span class="text-SeabiscuitDark200ThemeColor/50">• ${totalClassesLength}  entries</span>`,
      }

      let arrClasses: { label: string; value: string; order: number }[] =
        eventReviewPublish?.EventFees?.registrationFees?.flatMap((itm) => {
          const classesLength = sumBy(
            allRiders?.filter((r) =>
              r.classes.find((entry) => entry.registrationByDayName === itm.name)
            ),
            (entry) => size(entry.classes)
          )
          if (itm.children && Array.isArray(itm.children) && itm.children.length) {
            return itm.children.map((childrenItem) => {
              const childrenClassesLength = allRiders.reduce((count, rider) => {
                if (
                  childrenItem.name &&
                  rider.classes.find((entry) => entry.registrationByDayName === childrenItem.name)
                ) {
                  return count + 1
                }
                return count
              }, 0)

              return {
                value: childrenItem.name || '',
                label: `${childrenItem.order ? `#${childrenItem.order} •` : ''} ${childrenItem.name} <span class="text-SeabiscuitDark200ThemeColor/50">• ${childrenClassesLength} entries</span>`,
                order: Number(childrenItem.order) || 0,
              }
            })
          }

          return [
            {
              value: itm.name || '',
              label: `${itm.order ? `#${itm.order} •` : ''} ${itm.name} <span class="text-SeabiscuitDark200ThemeColor/50">• ${classesLength} entries</span>`,
              order: Number(itm.order) || 0,
            },
          ]
        }) || []

      arrClasses = arrClasses.sort((a, b) => (a?.order || 0) - (b?.order || 0)) || []

      const newRidersClasses: typeof ridersClasses = [allClassesLabel, ...arrClasses]

      setActiveClass(allClassesLabel)
      setRidersClasses(newRidersClasses)
    }
  }, [eventReviewPublish?.EventFees, allRiders])

  useEffect(() => {
    const { adminEntries } = getAdminEntries({
      registrationsByDay,
      fees,
      eventRegisteredUsers,
      riderTeamMembers,
      usersDocuments,
      horseCompetitionPaperwork,
      horses,
      eventReviewPublish,
      users,
    })
    // add documents to riders
    adminEntries.forEach((rider) => {
      rider['documents'] = usersDocuments.filter(
        (d) => d.signatoryId === rider.riderId
      ) as UserDocumentModel[]
    })

    setAllRiders(adminEntries)
  }, [
    eventReviewPublish,
    eventRegisteredUsers,
    registrationsByDay,
    riderTeamMembers,
    usersDocuments,
    users,
    fees,
    horses,
    horseCompetitionPaperwork,
  ])

  const getDataFromDb = async () => {
    try {
      dispatch(
        organizerSliceAcs.setExhibitorsAc({
          eventId,
          cursorId: null,
          tabName: 'all',
        })
      )

      const riderTeamMembersInDb = await RiderTeamMemberService.getByEventId(eventId)

      const eventRegisteredUsersInDb = await EventRegisteredUsersService.getByEventId(eventId)
      const usersDocumentsInDb = await UsersDocumentsService.getByEvent(eventId)
      const eventReviewPublishInDb = await EventReviewPublishService.getById(eventId)
      const feesInDb = await RegistrationFeesService.getByEventId(eventId)

      const registeredUsersByDay = await RegistrationDyDayService.getByEventIds([eventId])

      const userIds: Set<string> = new Set()
      registeredUsersByDay.forEach((item) => {
        if (item.riderId) userIds.add(item.riderId)
        if (item.userId) userIds.add(item.userId)
      })
      const usersInDb = await UserService.getByIds([...userIds])

      // get horses by "id" field from horsesSelected
      const horseIds: Set<string> = new Set()
      registeredUsersByDay.forEach((item) => {
        item.horsesSelected.forEach((horse) => {
          if (horse.id !== HORSE_NAME_DEFAULT) horseIds.add(horse.id)
        })
      })
      const horsesInDb = await HorsesService.getHorsesByIds([...horseIds])

      // get horse paperwork by "horseId" field from horses
      const horseHorseIds: Set<string> = new Set()
      horsesInDb.forEach((item) => {
        if (item.horseId) horseHorseIds.add(item.horseId)
      })
      const horseCompetitionPaperworkInDb = await HorseCompetitionPaperworkService.getByHorseIds([
        ...horseHorseIds,
      ])

      setRegistrationByDay(registeredUsersByDay)
      setFees(feesInDb)
      setUsersDocuments(usersDocumentsInDb)
      setRiderTeamMembers(riderTeamMembersInDb)
      setEventRegisteredUsers(eventRegisteredUsersInDb)
      setUsers(usersInDb)
      setEventReviewPublish(eventReviewPublishInDb)
      setHorseCompetitionPaperwork(horseCompetitionPaperworkInDb.filter((item) => item.horseId))
      setHorses(horsesInDb)
    } catch (error) {
      console.error(error, 'error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (eventId) getDataFromDb().then()
    return () => {
      dispatch(organizerSliceAcs.resetExhibitorAc())
    }
  }, [dispatch, eventId])

  useEffect(() => {
    let riders = sortRidersByScratched(allRiders)
    let draftRiders = ridersWithDraft(eventRegisteredUsers)

    // filter by class
    if (activeClass.value !== ALL_CLASSES.value) {
      riders = riders.filter((rider) => {
        return rider.classes.find((entry) => entry.registrationByDayName === activeClass.value)
      })
      draftRiders = draftRiders.filter((rider) => {
        return rider.classes.find((entry) => entry.registrationByDayName === activeClass.value)
      })
    }

    // filter by name
    if (searchNameValue.length >= 1) {
      riders = riders.filter(
        (rider) =>
          rider.riderName?.toLowerCase().includes(searchNameValue.toLowerCase()) ||
          rider.horseName?.toLowerCase().includes(searchNameValue.toLowerCase())
      )
      draftRiders = draftRiders.filter(
        (rider) =>
          rider.riderName?.toLowerCase().includes(searchNameValue.toLowerCase()) ||
          rider.horseName?.toLowerCase().includes(searchNameValue.toLowerCase())
      )
    }

    // filter by status
    if (activeStatus?.value === EStatuses.noIssues) {
      riders = getNoIssues(riders)
      draftRiders = getNoIssues(draftRiders)
    } else if (activeStatus?.value === EStatuses.issues) {
      riders = geIssues(riders)
      draftRiders = geIssues(draftRiders)
    } else if (activeStatus?.value === EStatuses.scratched) {
      riders = riders.filter((rider) => rider.isScratched)
      draftRiders = draftRiders.filter((rider) => rider.isScratched)
    }

    setFilteredRiders(riders)
    setDraftRiders(draftRiders)
  }, [activeClass, searchNameValue, activeStatus, allRiders, eventRegisteredUsers])

  function sortHandler(type: 'riderName' | 'backNumber' | 'horseName') {
    if (activeSortType !== ESorts.none) {
      const newRiders = filteredRiders.sort((a, b) => {
        const v1 = type === 'backNumber' ? +a[type] || 0 : a[type] || ''
        const v2 = type === 'backNumber' ? +b[type] || 0 : b[type] || ''

        if (v1 < v2) {
          return -1
        }
        if (v1 > v2) {
          return 1
        }
        return 0
      })
      setFilteredRiders(newRiders)
      setActiveSortType(ESorts.none)
    } else {
      const newRiders = filteredRiders.sort((a, b) => {
        const v1 = type === 'backNumber' ? +a[type] || 0 : a[type] || ''
        const v2 = type === 'backNumber' ? +b[type] || 0 : b[type] || ''

        if (v1 > v2) {
          return -1
        }
        if (v1 < v2) {
          return 1
        }
        return 0
      })
      setFilteredRiders(newRiders)
      switch (type) {
        case 'riderName':
          return setActiveSortType(ESorts.rider)
        case 'horseName':
          return setActiveSortType(ESorts.horse)
        default:
          return setActiveSortType(ESorts.back)
      }
    }
  }

  return {
    loading,
    tableColumns,
    filteredRiders,
    draftRiders,
    allRiders,
    activeTab,
    setActiveTab,
    activeClass,
    setActiveClass,
    searchNameValue,
    setSearchNameValue,
    activeStatus,
    setActiveStatus,
    ridersClasses,
    syncLoading,
  }
}
